import type { ParseKeys } from 'i18next'

import type { SeededRole } from '@/types/domain'

/* The access matrix, as reference data.
 *
 * This is PRD §3 transcribed — the same table, in the same order, with the same
 * boundary notes. It is rendered read-only in Settings so a founder can answer
 * "what can Pricing actually see?" without opening the PRD, and so the rules a
 * user runs into are written down somewhere inside the product.
 *
 * IMPORTANT: this file is DOCUMENTATION, not enforcement. Authorization is
 * server-side and only server-side (AD-21, FR-56); nothing here is consulted at
 * runtime to decide access, and no client-side check should ever read it. If
 * this table and the server ever disagree, the server is right and this file is
 * a bug — which is the reason each row cites its FR/AD, so the two can be
 * checked against each other by hand.
 *
 * Labels are i18n keys rather than text: this is module-level data evaluated
 * once at import, so a translated string baked in here would freeze in whatever
 * language was active at startup (the same reason navigation.ts holds keys).
 */

/** The five SHIPPED roles, in matrix column order.
 *
 *  This table documents what the product ships with. Roles are runtime-definable
 *  since migration 0021, so it is no longer the whole picture — a role a founder
 *  created has no column here and never will. The editable roles panel above it
 *  is where that lives; this stays because "what do the five built-in roles do"
 *  is still the question somebody asks first.
 *
 *  Typed as SeededRole rather than Role deliberately: every cell must be filled
 *  for every column, and an index signature over all strings would make each
 *  lookup optional and hide a genuinely missing row. */
export const MATRIX_ROLES: readonly SeededRole[] = [
  'sales',
  'pricing',
  'operations',
  'finance',
  'super-admin',
] as const

/* The five levels every cell resolves to. `scoped` is the interesting one: it
 * means "yes, but only for records this user is on" — the distinction the whole
 * visibility model turns on, and the one a plain yes/no grid would erase by
 * rounding it to yes. `hidden` is not a weaker `none`: it means the field is
 * absent from the DTO entirely (AD-6), which is why it reads differently. */
export type AccessLevel = 'full' | 'scoped' | 'read' | 'hidden' | 'none'

export interface MatrixRow {
  id: string
  /** Key into `settings.access.groups`. */
  labelKey: ParseKeys
  /** The FR/AD this row is transcribed from, shown so the two can be checked. */
  source: string
  levels: Record<SeededRole, AccessLevel>
  /** Key into `settings.access.detail` — the qualifier the level alone loses,
   *  e.g. that Sales creates clients but sees only its own. Keyed by role, and
   *  partial: most cells are fully described by their level. */
  detailKey?: Partial<Record<SeededRole, ParseKeys>>
}

/** PRD §3 access matrix, row for row and in document order. */
export const ACCESS_MATRIX: readonly MatrixRow[] = [
  {
    id: 'operational',
    labelKey: 'settings.access.groups.operational',
    source: 'FR-56 · AD-5',
    levels: {
      sales: 'scoped',
      pricing: 'scoped',
      operations: 'scoped',
      finance: 'full',
      'super-admin': 'full',
    },
    detailKey: {
      sales: 'settings.access.detail.ownOnly',
      pricing: 'settings.access.detail.assigned',
      operations: 'settings.access.detail.assigned',
    },
  },
  {
    id: 'commercial',
    labelKey: 'settings.access.groups.commercial',
    source: 'FR-56',
    levels: {
      sales: 'scoped',
      pricing: 'scoped',
      operations: 'scoped',
      finance: 'full',
      'super-admin': 'full',
    },
    detailKey: {
      sales: 'settings.access.detail.ownDeals',
      pricing: 'settings.access.detail.assigned',
      operations: 'settings.access.detail.assigned',
    },
  },
  {
    /* Field-level, not record-level: a Sales user sees the whole case, with
       agent identity absent from it (FR-29, AD-6). */
    id: 'agent-identity',
    labelKey: 'settings.access.groups.agentIdentity',
    source: 'FR-29 · AD-6',
    levels: {
      sales: 'hidden',
      pricing: 'full',
      operations: 'full',
      finance: 'full',
      'super-admin': 'full',
    },
  },
  {
    id: 'cross-case-finance',
    labelKey: 'settings.access.groups.crossCaseFinance',
    source: 'FR-56',
    levels: {
      sales: 'none',
      pricing: 'none',
      operations: 'none',
      finance: 'full',
      'super-admin': 'full',
    },
  },
  {
    id: 'pipeline',
    labelKey: 'settings.access.groups.pipeline',
    source: 'FR-15',
    levels: {
      sales: 'scoped',
      pricing: 'none',
      operations: 'none',
      finance: 'full',
      'super-admin': 'full',
    },
    detailKey: { sales: 'settings.access.detail.ownQuotes' },
  },
  {
    id: 'clients',
    labelKey: 'settings.access.groups.clients',
    source: 'FR-39 · FR-44',
    levels: {
      sales: 'scoped',
      pricing: 'read',
      operations: 'read',
      finance: 'full',
      'super-admin': 'full',
    },
    detailKey: {
      sales: 'settings.access.detail.readCreateOwn',
      'super-admin': 'settings.access.detail.salesDirectorGrant',
    },
  },
  {
    id: 'participants',
    labelKey: 'settings.access.groups.participants',
    source: 'FR-40 · FR-44',
    levels: {
      sales: 'hidden',
      pricing: 'full',
      operations: 'full',
      finance: 'read',
      'super-admin': 'full',
    },
    detailKey: {
      sales: 'settings.access.detail.participantsSales',
      pricing: 'settings.access.detail.maintainAgents',
      'super-admin': 'settings.access.detail.pricingDirectorGrant',
    },
  },
]

/** The FR-44 grants. Additive booleans on the user record, never roles (AD-7) —
 *  which is why they are listed apart from the matrix rather than as columns in
 *  it. Assigned per user in Directories → Users, not here. */
export const GRANTS = [
  {
    id: 'manageClients',
    labelKey: 'directories.users.grantLabels.manageClients',
    descriptionKey: 'settings.access.grants.manageClients',
  },
  {
    id: 'manageParticipants',
    labelKey: 'directories.users.grantLabels.manageParticipants',
    descriptionKey: 'settings.access.grants.manageParticipants',
  },
  {
    id: 'manageTransport',
    labelKey: 'directories.users.grantLabels.manageTransport',
    descriptionKey: 'settings.access.grants.manageTransport',
  },
] as const
